import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Brain, Users, Building2, ChevronRight, BookOpen, ArrowRight, CheckCircle, Zap } from 'lucide-react';

// ── Componente de Passo do Fluxo ─────────────────────────────
function FlowStep({ numero, emoji, titulo, desc, cor = 'bg-acre-green', last = false }) {
  return (
    <div className="flex items-start gap-0">
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={`w-11 h-11 rounded-full ${cor} text-white flex items-center justify-center font-sora font-bold text-sm shadow-md`}>
          {numero}
        </div>
        {!last && <div className="w-0.5 h-10 bg-gray-200 mt-1"/>}
      </div>
      <div className="ml-4 pb-8">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xl">{emoji}</span>
          <p className="font-sora font-bold text-acre-dark text-sm">{titulo}</p>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

// ── Fluxograma SVG ────────────────────────────────────────────
function FluxogramaAlgoritmo() {
  const BOX = ({ x, y, w = 160, h = 44, fill = '#1a6b45', text, sub, rx = 10 }) => (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={rx} fill={fill} opacity="0.92"/>
      <text x={x+w/2} y={y+h/2-(sub?6:0)} textAnchor="middle" fill="white"
        fontSize="11" fontWeight="bold" fontFamily="Arial">{text}</text>
      {sub && <text x={x+w/2} y={y+h/2+10} textAnchor="middle" fill="rgba(255,255,255,0.7)"
        fontSize="9" fontFamily="Arial">{sub}</text>}
    </g>
  );
  const ARR = ({ x1,y1,x2,y2 }) => (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#9ca3af" strokeWidth="1.5" markerEnd="url(#arr)"/>
    </g>
  );
  const PESO = ({ x, y, pct, label, cor }) => (
    <g>
      <rect x={x} y={y} width={80} height={28} rx={6} fill={cor} opacity="0.15"/>
      <rect x={x} y={y} width={80} height={28} rx={6} fill="none" stroke={cor} strokeWidth="1"/>
      <text x={x+40} y={y+11} textAnchor="middle" fill={cor} fontSize="11" fontWeight="bold" fontFamily="Arial">{pct}</text>
      <text x={x+40} y={y+23} textAnchor="middle" fill={cor} fontSize="8" fontFamily="Arial">{label}</text>
    </g>
  );

  return (
    <svg viewBox="0 0 540 380" className="w-full max-h-96">
      <defs>
        <marker id="arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#9ca3af"/>
        </marker>
      </defs>

      {/* Candidato */}
      <BOX x={20} y={30} w={120} h={44} fill="#0f2318" text="Perfil do" sub="Candidato"/>
      {/* Vaga */}
      <BOX x={400} y={30} w={120} h={44} fill="#0f2318" text="Requisitos da" sub="Vaga"/>

      {/* Arrows para algoritmo */}
      <ARR x1={140} y1={52} x2={200} y2={52}/>
      <ARR x1={400} y1={52} x2={340} y2={52}/>

      {/* Algoritmo central */}
      <rect x={195} y={20} width={150} height={64} rx={12} fill="#1a6b45" opacity="0.95"/>
      <text x={270} y={46} textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" fontFamily="Arial">Algoritmo</text>
      <text x={270} y={60} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="9" fontFamily="Arial">Match AcreJob v2.0</text>
      <text x={270} y={75} textAnchor="middle" fill="#fcd34d" fontSize="9" fontFamily="Arial">5 dimensões</text>

      {/* Seta para baixo */}
      <ARR x1={270} y1={84} x2={270} y2={115}/>

      {/* Pesos */}
      <text x={270} y={110} textAnchor="middle" fill="#374151" fontSize="10" fontFamily="Arial" fontWeight="bold">Score Final = Σ(peso × dimensão)</text>

      <PESO x={20}  y={125} pct="30%" label="Área"        cor="#1a6b45"/>
      <PESO x={115} y={125} pct="20%" label="Localização" cor="#2563eb"/>
      <PESO x={210} y={125} pct="15%" label="Escolaridade"cor="#7c3aed"/>
      <PESO x={305} y={125} pct="15%" label="Experiência" cor="#d97706"/>
      <PESO x={400} y={125} pct="20%" label="Hard Skills"  cor="#dc2626"/>

      {/* Linha de pesos */}
      <line x1={60} y1={153} x2={270} y2={175} stroke="#9ca3af" strokeWidth="1" strokeDasharray="3,2"/>
      <line x1={155} y1={153} x2={270} y2={175} stroke="#9ca3af" strokeWidth="1" strokeDasharray="3,2"/>
      <line x1={250} y1={153} x2={270} y2={175} stroke="#9ca3af" strokeWidth="1" strokeDasharray="3,2"/>
      <line x1={345} y1={153} x2={270} y2={175} stroke="#9ca3af" strokeWidth="1" strokeDasharray="3,2"/>
      <line x1={440} y1={153} x2={270} y2={175} stroke="#9ca3af" strokeWidth="1" strokeDasharray="3,2"/>

      {/* Score */}
      <BOX x={195} y={175} w={150} h={44} fill="#c8a035" text="Score de Match" sub="0% – 100%"/>
      <ARR x1={270} y1={219} x2={270} y2={250}/>

      {/* Classificação */}
      {[
        {x:20,  fill:'#22c55e', label:'≥90%',  sub:'Excelente'},
        {x:115, fill:'#84cc16', label:'70–89%', sub:'Ótimo'},
        {x:210, fill:'#f59e0b', label:'50–69%', sub:'Bom'},
        {x:305, fill:'#f97316', label:'30–49%', sub:'Moderado'},
        {x:400, fill:'#ef4444', label:'<30%',   sub:'Baixo'},
      ].map((c,i) => (
        <g key={i}>
          <line x1={270} y1={250} x2={c.x+40} y2={270} stroke="#d1d5db" strokeWidth="1"/>
          <PESO x={c.x} y={270} pct={c.label} label={c.sub} cor={c.fill}/>
        </g>
      ))}

      {/* Resultado */}
      <ARR x1={270} y1={298} x2={270} y2={325}/>
      <BOX x={155} y={325} w={230} h={44} fill="#0f2318" text="Candidatos rankeados" sub="do maior para o menor score" rx={12}/>
    </svg>
  );
}

// ── Seção de referências ──────────────────────────────────────
const REFERENCIAS = [
  {
    autores: 'Gale, D. & Shapley, L. S.',
    ano: '1962',
    titulo: 'College Admissions and the Stability of Marriage',
    fonte: 'American Mathematical Monthly, 69(1), 9–15.',
    doi: 'https://doi.org/10.2307/2312726',
    desc: 'Base teórica para o problema de matching estável entre dois conjuntos — candidatos e vagas.',
  },
  {
    autores: 'Salton, G. & Buckley, C.',
    ano: '1988',
    titulo: 'Term-weighting approaches in automatic text retrieval',
    fonte: 'Information Processing & Management, 24(5), 513–523.',
    doi: 'https://doi.org/10.1016/0306-4573(88)90021-0',
    desc: 'Fundamento da similaridade de cosseno usada no matching de habilidades técnicas (hard skills).',
  },
  {
    autores: 'Malinowski, J., Keim, T. & Wendt, O.',
    ano: '2006',
    titulo: 'Matching People and Jobs: A Bilateral Recommendation System',
    fonte: 'Proceedings of the 39th Annual HICSS, IEEE.',
    doi: 'https://doi.org/10.1109/HICSS.2006.266',
    desc: 'Modelo de recomendação bilateral aplicado a recrutamento, base para matching multidimensional.',
  },
  {
    autores: 'Fazel-Zarandi, M. & Fox, M. S.',
    ano: '2009',
    titulo: 'Semantic Matchmaking for Job Recruitment: An Ontology-Based Hybrid Approach',
    fonte: 'Proceedings of the 5th International Semantic Web Conference.',
    desc: 'Uso de ontologias para correspondência semântica de competências e requisitos de vagas.',
  },
  {
    autores: 'Al-Otaibi, S. T. & Ykhlef, M.',
    ano: '2012',
    titulo: 'A Survey of Job Recommender Systems',
    fonte: 'International Journal of Physical Sciences, 7(29), 5127–5142.',
    desc: 'Revisão sistemática dos principais métodos de recomendação de empregos na literatura.',
  },
];

// ════════════════════════════════════════════════════════════
export default function ComoFunciona() {
  const [aba, setAba] = useState('candidato'); // candidato | empresa | algoritmo | literatura

  return (
    <div className="min-h-screen bg-[#f9fafb]">
      <Navbar/>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0a1f12] via-[#0f2318] to-[#1a3820] py-16 px-5 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 bg-acre-gold/15 text-acre-gold border border-acre-gold/25 text-xs font-bold px-4 py-1.5 rounded-full mb-5">
            🧠 Tecnologia & Transparência
          </span>
          <h1 className="font-sora text-3xl md:text-5xl font-extrabold text-white mb-4">
            Como o AcreJob<br/>
            <span className="bg-gradient-to-r from-acre-gold to-yellow-300 bg-clip-text text-transparent">
              conecta talentos e empresas
            </span>
          </h1>
          <p className="text-white/60 text-base max-w-xl mx-auto leading-relaxed">
            Entenda o algoritmo de match multidimensional, o fluxo completo para candidatos e
            empresas, e as referências científicas que embasam nossa tecnologia.
          </p>
        </div>
      </section>

      {/* Abas */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-5">
          <div className="flex gap-0 overflow-x-auto">
            {[
              { id:'candidato',  label:'👤 Para Candidatos',   icon:Users },
              { id:'empresa',    label:'🏢 Para Empresas',     icon:Building2 },
              { id:'algoritmo',  label:'🧠 O Algoritmo',       icon:Brain },
              { id:'literatura', label:'📚 Referências',       icon:BookOpen },
            ].map(a => (
              <button key={a.id} onClick={() => setAba(a.id)}
                className={`flex items-center gap-1.5 px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  aba === a.id
                    ? 'border-acre-green text-acre-green'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {a.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-12">

        {/* ── CANDIDATOS ── */}
        {aba === 'candidato' && (
          <div>
            <div className="text-center mb-10">
              <h2 className="font-sora text-2xl font-extrabold text-acre-dark mb-2">
                Sua jornada no AcreJob
              </h2>
              <p className="text-gray-500 text-sm">Do cadastro à contratação — 4 fases simples</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Fluxo */}
              <div className="card p-6">
                <p className="font-sora font-bold text-sm text-acre-dark mb-5 flex items-center gap-2">
                  <Zap size={15} className="text-acre-green"/> Fluxo do Candidato
                </p>
                <FlowStep numero="1" emoji="🤖" titulo="Crie seu perfil com a Mikhal IA"
                  desc="Responda 17 perguntas sobre Hard Skills, Soft Skills e Fit Cultural. A IA gera automaticamente seu perfil profissional completo em minutos."/>
                <FlowStep numero="2" emoji="💚" titulo="Explore vagas compatíveis"
                  desc="O algoritmo calcula o % de compatibilidade entre seu perfil e cada vaga aberta. Vagas com ≥70% aparecem na sua lista ordenadas do maior para o menor match."/>
                <FlowStep numero="3" emoji="👍" titulo="Demonstre interesse"
                  desc="Dê 'like' nas vagas que te interessam. A empresa recebe uma notificação de que você está disponível para a oportunidade."/>
                <FlowStep numero="4" emoji="🤝" titulo="Entre em contato" last
                  desc="Empresas que escolherem seu perfil entrarão em contato pelo chat integrado. Você também pode iniciar a conversa se o match for ≥70%."/>
              </div>

              {/* Como o score é calculado */}
              <div className="space-y-4">
                <div className="card p-5">
                  <p className="font-sora font-bold text-sm text-acre-dark mb-3">
                    📊 Como seu score é calculado
                  </p>
                  {[
                    { pct:'30%', label:'Área de atuação',   desc:'Suas áreas de interesse vs. as áreas da vaga',    cor:'bg-acre-green' },
                    { pct:'20%', label:'Localização',        desc:'Proximidade geográfica com a cidade da vaga',      cor:'bg-blue-500' },
                    { pct:'15%', label:'Escolaridade',       desc:'Seu nível vs. o requisito mínimo da vaga',         cor:'bg-purple-500' },
                    { pct:'15%', label:'Experiência',        desc:'Seus anos de exp. vs. o mínimo requerido',         cor:'bg-amber-500' },
                    { pct:'20%', label:'Hard Skills',        desc:'Suas habilidades técnicas vs. o que a vaga exige', cor:'bg-red-500' },
                  ].map((d,i) => (
                    <div key={i} className="flex items-center gap-3 mb-2.5 last:mb-0">
                      <span className={`w-10 h-6 rounded-full ${d.cor} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>
                        {d.pct}
                      </span>
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{d.label}</p>
                        <p className="text-xs text-gray-400">{d.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="card p-5 bg-gradient-to-br from-acre-light to-white border border-acre-green/20">
                  <p className="font-sora font-bold text-sm text-acre-dark mb-3">
                    💡 Como melhorar seu score
                  </p>
                  {[
                    'Complete o perfil com a Mikhal IA — Hard Skills são 20% do score',
                    'Adicione suas áreas de interesse corretamente',
                    'Informe sua cidade para o score de localização',
                    'Preencha escolaridade e anos de experiência',
                    'Vagas com ≥70% aparecem automaticamente para você',
                  ].map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 mb-1.5 last:mb-0">
                      <CheckCircle size={13} className="text-acre-green flex-shrink-0 mt-0.5"/>
                      <p className="text-xs text-gray-600">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── EMPRESAS ── */}
        {aba === 'empresa' && (
          <div>
            <div className="text-center mb-10">
              <h2 className="font-sora text-2xl font-extrabold text-acre-dark mb-2">
                Contratação inteligente com IA
              </h2>
              <p className="text-gray-500 text-sm">Da publicação da vaga à contratação do candidato ideal</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="card p-6">
                <p className="font-sora font-bold text-sm text-acre-dark mb-5 flex items-center gap-2">
                  <Zap size={15} className="text-acre-green"/> Fluxo da Empresa
                </p>
                <FlowStep numero="1" emoji="📋" titulo="Publique a vaga com pré-requisitos"
                  desc="Preencha título, descrição, área, localização. Quanto mais detalhados os pré-requisitos (escolaridade, experiência, hard skills), mais preciso será o match."/>
                <FlowStep numero="2" emoji="🧠" titulo="IA rankeia candidatos automaticamente"
                  desc="O algoritmo analisa todos os candidatos cadastrados e gera um ranking ordenado por % de compatibilidade com sua vaga — em 5 dimensões."/>
                <FlowStep numero="3" emoji="💬" titulo="Selecione e converse"
                  desc="Veja o perfil completo (hard skills, soft skills, fit cultural, experiências). Candidates com ≥80% desbloqueiam chat direto. Agende entrevistas pela plataforma."/>
                <FlowStep numero="4" emoji="✅" titulo="Contrate e feche a vaga" last
                  desc="Aprove o candidato ideal, envie a mensagem de aprovação com a ajuda da Mikhal IA e registre a contratação no sistema. Feche a vaga com 1 clique."/>
              </div>

              <div className="space-y-4">
                <div className="card p-5">
                  <p className="font-sora font-bold text-sm text-acre-dark mb-3">
                    🎯 O que define a qualidade do match
                  </p>
                  {[
                    { emoji:'📍', titulo:'Pré-requisitos detalhados', desc:'Escolaridade, experiência e hard skills são 50% do score. Vagas genéricas recebem candidatos menos qualificados.' },
                    { emoji:'🌿', titulo:'Áreas bem definidas',       desc:'A área de atuação é 30% do score. Marque apenas as áreas que realmente correspondem à vaga.' },
                    { emoji:'🏙️', titulo:'Cidade correta',            desc:'A localização é 20% do score. Candidatos da mesma cidade ou região têm maior compatibilidade.' },
                    { emoji:'💡', titulo:'Hard Skills específicas',   desc:'Adicionar habilidades técnicas exatas (ex: "Power BI", "SAP FI") refina o match com especialistas.' },
                  ].map((d,i) => (
                    <div key={i} className="flex items-start gap-3 mb-3 last:mb-0">
                      <span className="text-2xl flex-shrink-0">{d.emoji}</span>
                      <div>
                        <p className="text-xs font-semibold text-gray-700">{d.titulo}</p>
                        <p className="text-xs text-gray-400">{d.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="card p-5 bg-gradient-to-br from-blue-50 to-white border border-blue-100">
                  <p className="font-sora font-bold text-sm text-acre-dark mb-2">📈 Benefícios do sistema</p>
                  {[
                    'Candidatos rankeados por compatibilidade real',
                    'Redução do tempo de triagem em até 80%',
                    'Perfis com hard skills, soft skills e fit cultural',
                    'Chat integrado — sem sair da plataforma',
                    'Histórico de todo o processo seletivo',
                  ].map((b,i) => (
                    <div key={i} className="flex items-center gap-2 mb-1.5 last:mb-0">
                      <CheckCircle size={13} className="text-blue-500 flex-shrink-0"/>
                      <p className="text-xs text-gray-600">{b}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ALGORITMO ── */}
        {aba === 'algoritmo' && (
          <div>
            <div className="text-center mb-10">
              <h2 className="font-sora text-2xl font-extrabold text-acre-dark mb-2">
                Algoritmo de Match Multidimensional
              </h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">
                Baseado em similaridade de cosseno (Salton, 1988) e modelo de pesos ponderados
                (Malinowski et al., 2006) para matching bilateral candidato–vaga.
              </p>
            </div>

            {/* Fluxograma */}
            <div className="card p-6 mb-6">
              <p className="font-sora font-bold text-sm text-acre-dark mb-4 text-center">
                Fluxo de Cálculo do Score
              </p>
              <FluxogramaAlgoritmo/>
            </div>

            {/* Fórmula */}
            <div className="card p-6 mb-6 bg-acre-dark text-white font-mono">
              <p className="text-acre-gold text-xs mb-3 font-semibold uppercase tracking-wider">// Fórmula do Score Final</p>
              <p className="text-sm leading-relaxed">
                <span className="text-green-300">Score</span>
                <span className="text-white/60"> = </span>
                <span className="text-yellow-300">scoreÁrea</span> × <span className="text-white/80">0.30</span>
                <span className="text-white/60"> + </span>
                <span className="text-blue-300">scoreGeo</span> × <span className="text-white/80">0.20</span>
                <span className="text-white/60"> + </span>
                <span className="text-purple-300">scoreEsc</span> × <span className="text-white/80">0.15</span>
                <span className="text-white/60"> + </span>
                <span className="text-amber-300">scoreExp</span> × <span className="text-white/80">0.15</span>
                <span className="text-white/60"> + </span>
                <span className="text-red-300">scoreSkills</span> × <span className="text-white/80">0.20</span>
              </p>
              <p className="text-white/40 text-xs mt-3">// Similaridade de cosseno para área e skills:</p>
              <p className="text-sm mt-1">
                <span className="text-green-300">cos(A,B)</span>
                <span className="text-white/60"> = |A∩B| / √(|A| × |B|)</span>
                <span className="text-white/40">  // Salton & Buckley, 1988</span>
              </p>
            </div>

            {/* Dimensões detalhadas */}
            <div className="grid md:grid-cols-2 gap-4">
              {[
                {
                  pct:'30%', cor:'border-acre-green bg-acre-light/50', titleCor:'text-acre-green',
                  titulo:'Área de Atuação', metodo:'Similaridade de Cosseno',
                  desc:'Compara o conjunto de áreas de interesse do candidato com as áreas de contratação da vaga. Usa similaridade de cosseno entre os dois conjuntos para calcular a sobreposição relativa.',
                  formula:'cos(Ac, Av) = |Ac ∩ Av| / √(|Ac| × |Av|)',
                },
                {
                  pct:'20%', cor:'border-blue-300 bg-blue-50/50', titleCor:'text-blue-600',
                  titulo:'Proximidade Geográfica', metodo:'Score por Região',
                  desc:'Candidatos na mesma cidade: 100%. Mesma região do Acre (ex: Vale do Juruá): 50%. Regiões diferentes: 0%. Baseado nos 22 municípios e 4 regiões do estado.',
                  formula:'scoreGeo ∈ {0, 50, 100}',
                },
                {
                  pct:'15%', cor:'border-purple-300 bg-purple-50/50', titleCor:'text-purple-600',
                  titulo:'Nível de Escolaridade', metodo:'Comparação Ordinal',
                  desc:'Mapeia escolaridades em escala ordinal (1–7). Candidato atende ou supera o requisito: 100%. Um nível abaixo: 60%. Dois níveis abaixo: 30%. Abaixo disso: 0%.',
                  formula:'scoreEsc = f(NivCand ≥ NivReq)',
                },
                {
                  pct:'15%', cor:'border-amber-300 bg-amber-50/50', titleCor:'text-amber-600',
                  titulo:'Experiência Profissional', metodo:'Razão Linear',
                  desc:'Calcula a proporção entre os anos de experiência do candidato e o mínimo requerido pela vaga. Se superior ou igual ao requisito, score = 100%.',
                  formula:'scoreExp = min(1, anosCandidato / anosRequeridos) × 100',
                },
                {
                  pct:'20%', cor:'border-red-300 bg-red-50/50', titleCor:'text-red-600',
                  titulo:'Hard Skills Técnicas', metodo:'Cosseno + Fuzzy Matching',
                  desc:'Compara as hard skills do candidato com as skills requeridas pela vaga usando similaridade de cosseno. Aplica Fuzzy Matching (distância de Levenshtein ≤ 2) para variações como "Excel" e "Excel Avançado".',
                  formula:'Levenshtein(s1, s2) ≤ 2 → match válido',
                },
              ].map((d,i) => (
                <div key={i} className={`card p-4 border-l-4 ${d.cor}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className={`font-sora font-bold text-sm ${d.titleCor}`}>{d.titulo}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${d.titleCor} bg-white border`}>{d.pct}</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-1.5 italic">{d.metodo}</p>
                  <p className="text-xs text-gray-600 leading-relaxed mb-2">{d.desc}</p>
                  <code className="block bg-gray-100 text-gray-700 text-xs px-2 py-1.5 rounded-lg font-mono">{d.formula}</code>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── LITERATURA ── */}
        {aba === 'literatura' && (
          <div>
            <div className="text-center mb-10">
              <h2 className="font-sora text-2xl font-extrabold text-acre-dark mb-2">
                Base Científica
              </h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">
                O algoritmo do AcreJob é fundamentado em pesquisas consagradas das áreas de
                Recuperação de Informação, Teoria dos Jogos e Sistemas de Recomendação.
              </p>
            </div>

            <div className="space-y-4">
              {REFERENCIAS.map((ref, i) => (
                <div key={i} className="card p-5 hover:shadow-md transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-acre-dark flex items-center justify-center text-acre-gold font-sora font-bold text-sm flex-shrink-0">
                      [{i+1}]
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-acre-dark">{ref.titulo}</p>
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full flex-shrink-0">{ref.ano}</span>
                      </div>
                      <p className="text-xs text-acre-green font-medium mt-0.5">{ref.autores}</p>
                      <p className="text-xs text-gray-400 mt-0.5 italic">{ref.fonte}</p>
                      <p className="text-xs text-gray-600 mt-2 leading-relaxed">{ref.desc}</p>
                      {ref.doi && (
                        <a href={ref.doi} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-500 hover:underline mt-1.5">
                          <ArrowRight size={11}/> Acessar publicação
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 card p-5 bg-acre-dark text-white text-center">
              <p className="font-sora font-bold mb-2">🌿 AcreJob — Tecnologia com propósito</p>
              <p className="text-white/60 text-sm">
                Construído para democratizar o acesso a oportunidades de emprego no Acre,
                combinando rigor científico com simplicidade de uso.
              </p>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-sm mb-4">Pronto para começar?</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/cadastro?tipo=candidato"
              className="btn-primary flex items-center gap-2 px-6 py-3">
              <Users size={15}/> Cadastrar como Candidato
            </Link>
            <Link to="/cadastro?tipo=empresa"
              className="btn-gold flex items-center gap-2 px-6 py-3">
              <Building2 size={15}/> Cadastrar Empresa
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
